import React, { useEffect, useState, useContext, useRef } from "react";
import AutoFitText from "../common/AutoFitText";
import SocketContext from "../../context/SocketContext";
import Modal from "../common/Modal";

export default function PokerGame({ gameState, sendAction, onGameAction }) {
  const socket = useContext(SocketContext);
  const [local, setLocal] = useState({});
  const [peekFor, setPeekFor] = useState(null);
  const [showRaisePopup, setShowRaisePopup] = useState(false);
  const [raiseAmount, setRaiseAmount] = useState(0);
  const [visibleAction, setVisibleAction] = useState(null);
  const [payoutStageIndex, setPayoutStageIndex] = useState(0);
  const peekForRef = useRef(null);
  const actionHandlerRef = useRef(() => {});
  const peekDurationMs = 2000;
  // no client-side auto next-round countdown; host must manually trigger nextRound

  const boardShellStyle = {
    background:
      "radial-gradient(circle at top, rgba(255,255,255,0.12), transparent 30%), linear-gradient(180deg, #0f5d44 0%, #0b3f31 100%)",
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
  };

  const seatShellStyle = {
    background:
      "radial-gradient(circle at top, rgba(255,255,255,0.08), transparent 34%), linear-gradient(180deg, rgba(16,94,69,0.9) 0%, rgba(8,53,41,0.92) 100%)",
  };

  const tableSurfaceStyle = {
    background:
      "radial-gradient(circle at top, rgba(255,255,255,0.12), transparent 38%), linear-gradient(180deg, rgba(0,0,0,0.12), rgba(0,0,0,0.22))",
  };

  // helper to parse card code or object into {rank,suit}
  const parseCard = (c) => {
    if (!c) return { rank: "", suit: "" };
    if (typeof c === "string") {
      const rankMap = { T: "10", J: "J", Q: "Q", K: "K", A: "A" };
      const suitMap = { s: "spades", h: "hearts", d: "diamonds", c: "clubs" };
      const r = c[0];
      const s = c[1];
      return { rank: rankMap[r] || r, suit: suitMap[s] || "" };
    }
    return {
      rank: c.rank || c.value || c.code || "",
      suit: c.suit || c.s || "",
    };
  };

  // size: 'player' (default, larger) or 'small' (for community)
  const renderCompactCard = (cardLike, key, size = "player") => {
    // cardLike may be a single card object/string or a pile (array)
    const top = Array.isArray(cardLike)
      ? cardLike[cardLike.length - 1] || {}
      : cardLike || {};
    const { rank, suit } = parseCard(top);
    const suitSymbols = { hearts: "♥", diamonds: "♦", clubs: "♣", spades: "♠" };
    const isRed = suit === "hearts" || suit === "diamonds";
    const dims =
      size === "small" ? { width: 32, height: 48 } : { width: 44, height: 64 };
    return (
      <div
        key={key}
        style={{
          width: dims.width,
          height: dims.height,
          borderRadius: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
          border: "1px solid rgba(0,0,0,0.06)",
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
        }}
      >
        <div style={{ textAlign: "center", lineHeight: 1 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: isRed ? "#b91c1c" : "#111",
            }}
          >
            {String(rank)}
          </div>
          <div style={{ fontSize: 20, color: isRed ? "#b91c1c" : "#111" }}>
            {suitSymbols[suit] || ""}
          </div>
        </div>
      </div>
    );
  };

  // Blind timer logic
  const [blindCountdown, setBlindCountdown] = useState(null);
  useEffect(() => {
    const intervalMins = gameState?.config?.blindInterval || 10;
    const intervalMs = intervalMins * 60 * 1000;
    // Use a timestamp for when blinds last doubled, or game start
    const startedAt = gameState?.startedAt
      ? new Date(gameState.startedAt).getTime()
      : Date.now();
    const lastBlindDoubledAt = gameState?.lastBlindDoubledAt
      ? new Date(gameState.lastBlindDoubledAt).getTime()
      : startedAt;
    if (!lastBlindDoubledAt) {
      setBlindCountdown(null);
      return;
    }
    const updateCountdown = () => {
      const now = Date.now();
      const elapsed = now - lastBlindDoubledAt;
      const remaining = Math.max(0, intervalMs - elapsed);
      setBlindCountdown(Math.ceil(remaining / 1000));
    };
    updateCountdown();
    const t = setInterval(updateCountdown, 1000);
    return () => clearInterval(t);
  }, [
    gameState?.config?.blindInterval,
    gameState?.lastBlindDoubledAt,
    gameState?.startedAt,
    gameState?.round,
  ]);

  const formatBlindCountdown = (secs) => {
    if (secs == null) return "";
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const renderCompactBack = (
    key,
    size = "small",
    folded = false,
    peeking = false
  ) => {
    const dims =
      size === "small" ? { width: 32, height: 48 } : { width: 44, height: 64 };
    return (
      <div
        key={key}
        style={{
          position: "relative",
          overflow: "hidden",
          width: dims.width,
          height: dims.height,
          borderRadius: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: "url('/cards/card_back.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          border: "1px solid rgba(255,255,255,0.06)",
          opacity: folded ? 0.6 : 1,
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
        }}
      >
        {peeking && !folded && (
          <>
            <style>{`
              @keyframes peekEyePulse {
                0%, 100% { opacity: 0.92; transform: scale(0.98); }
                50% { opacity: 1; transform: scale(1.08); }
              }
              @keyframes peekEyeBlink {
                0%, 42%, 100% { transform: scaleY(1); }
                48%, 54% { transform: scaleY(0.18); }
              }
            `}</style>
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(15,23,42,0.32)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "peekEyePulse 900ms ease-in-out infinite",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "2px 3px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.22)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.32)",
                }}
              >
                <svg
                  viewBox="0 0 32 22"
                  aria-hidden="true"
                  style={{
                    width: Math.max(18, Math.round(dims.width * 0.68)),
                    height: Math.max(14, Math.round(dims.height * 0.44)),
                    overflow: "visible",
                  }}
                >
                  <g
                    style={{
                      transformOrigin: "50% 50%",
                      animation: "peekEyeBlink 1800ms ease-in-out infinite",
                    }}
                  >
                    <path
                      d="M3 11C6.7 5.5 12.1 3 16 3s9.3 2.5 13 8c-3.7 5.5-9.1 8-13 8s-9.3-2.5-13-8Z"
                      fill="rgba(255,255,255,0.98)"
                      stroke="rgba(15,23,42,0.92)"
                      strokeWidth="2.1"
                    />
                    <ellipse cx="16" cy="11" rx="5.1" ry="5.1" fill="rgba(15,23,42,0.96)" />
                    <ellipse cx="17.7" cy="9.2" rx="1.5" ry="1.1" fill="rgba(255,255,255,0.98)" />
                  </g>
                </svg>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    setLocal(gameState || {});
  }, [gameState]);

  useEffect(() => {
    actionHandlerRef.current = onGameAction || sendAction || (() => {});
  }, [onGameAction, sendAction]);

  useEffect(() => {
    peekForRef.current = peekFor;
  }, [peekFor]);

  const latestAction = gameState?.lastAction || null;

  useEffect(() => {
    if (!latestAction?.sequence) {
      setVisibleAction(null);
      return undefined;
    }
    setVisibleAction(latestAction);
    const timeoutId = setTimeout(() => {
      setVisibleAction((current) =>
        current?.sequence === latestAction.sequence ? null : current
      );
    }, 4500);
    return () => clearTimeout(timeoutId);
  }, [latestAction]);

  useEffect(() => {
    setVisibleAction(null);
  }, [gameState?.round, gameState?.stage, gameState?.finished]);

  // Local countdown for auto Next Round: when a hand finishes, start a 5s countdown
  const [nextCountdown, setNextCountdown] = useState(null);
  const finishedFlag = !!(local && local.finished);
  const finishedAtTime =
    local && local.finishedAt ? new Date(local.finishedAt).getTime() : null;
  const gameOver = !!(local && local.result && local.result.outcome === "win");
  const resultsDisplayMs = Math.max(
    10000,
    Math.min(120000, Number(local?.config?.resultsDisplaySeconds || 30) * 1000)
  );
  useEffect(() => {
    if (gameOver) {
      setNextCountdown(null);
      return undefined;
    }

    if (finishedFlag) {
      const finishedAt = finishedAtTime || Date.now();
      const elapsed = Math.max(0, Date.now() - finishedAt);
      const remaining = Math.max(0, resultsDisplayMs - elapsed);
      setNextCountdown(Math.ceil(remaining / 1000));
      const t = setInterval(() => {
        setNextCountdown((v) => {
          if (!v) {
            clearInterval(t);
            return 0;
          }
          if (v <= 1) {
            clearInterval(t);
            try {
              if (!gameOver) actionHandlerRef.current({ type: "nextRound" });
            } catch (e) {}
            return 0;
          }
          return v - 1;
        });
      }, 1000);
      return () => clearInterval(t);
    }
    setNextCountdown(null);
    return undefined;
  }, [finishedFlag, finishedAtTime, gameOver, resultsDisplayMs]);

  useEffect(() => {
    if (peekForRef.current) {
      try {
        actionHandlerRef.current({ type: "peekCards", active: false });
      } catch (e) {}
    }
    setPeekFor(null);
  }, [gameState?.round, gameState?.started, gameState?.finished, gameState?.result]);

  useEffect(() => {
    if (!peekFor) return undefined;
    const timeoutId = setTimeout(() => {
      try {
        actionHandlerRef.current({ type: "peekCards", active: false });
      } catch (e) {}
      setPeekFor(null);
    }, peekDurationMs);

    return () => clearTimeout(timeoutId);
  }, [peekDurationMs, peekFor]);

  useEffect(() => {
    const stages = Array.isArray(local?.result?.breakdown)
      ? local.result.breakdown
      : [];
    const showingResults = !!(local?.finished && stages.length);

    if (!showingResults || stages.length <= 1) {
      setPayoutStageIndex(0);
      return undefined;
    }

    setPayoutStageIndex(0);
    let activeIndex = 0;
    const intervalId = setInterval(() => {
      activeIndex += 1;
      if (activeIndex >= stages.length) {
        clearInterval(intervalId);
        return;
      }
      setPayoutStageIndex(activeIndex);
    }, 2800);

    return () => clearInterval(intervalId);
  }, [local?.finished, local?.finishedAt, local?.result]);

  if (!local || !local.seats)
    return (
      <div className="p-4 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded">
        No poker state
      </div>
    );

  const seats = local.seats || [];
  const mySeatIndex = seats.findIndex(
    (s) => socket && socket.id === s.socketId
  );
  const rotated =
    mySeatIndex > 0
      ? seats.slice(mySeatIndex).concat(seats.slice(0, mySeatIndex))
      : seats.slice();
  const N = rotated.length;
  const top = N > 0 ? [rotated[0]] : [];
  const left = [];
  const right = [];
  const bottom = [];
  if (N > 1) {
    const bottomIndex = Math.floor(N / 2);
    bottom.push(rotated[bottomIndex]);
    for (let i = 1; i < bottomIndex; i++) right.push(rotated[i]);
    for (let i = bottomIndex + 1; i < N; i++) left.push(rotated[i]);
  }

  const potsSum =
    (local.pots || []).reduce((a, b) => a + (b.amount || 0), 0) ||
    local.pot ||
    0;
  const outstandingBets = (local.seats || []).reduce(
    (a, s) => a + (s.bet || 0),
    0
  );
  const displayedPot = potsSum + outstandingBets;

  const activeSeats = (local.seats || []).filter((s) => !s.folded);
  const activeNotAllIn = activeSeats.filter((s) => !s.allIn);
  const bettingClosedGlobal =
    !!local.finished ||
    local.stage === "showdown" ||
    !!(local.result && local.result.showdown) ||
    activeNotAllIn.length === 0;

  const mySeatIdxGlobal = (local.seats || []).findIndex(
    (s) => socket && socket.id === s.socketId
  );
  const mySeatGlobal =
    mySeatIdxGlobal !== -1 ? (local.seats || [])[mySeatIdxGlobal] : {};
  const currentBetGlobal = local.currentBet || 0;
  const toCallGlobal = Math.max(0, currentBetGlobal - (mySeatGlobal.bet || 0));
  const canIncreaseBetGlobal = !!(local.seats || []).some(
    (seat) =>
      seat.socketId !== mySeatGlobal.socketId &&
      !seat.folded &&
      !seat.allIn &&
      (seat.chips || 0) > 0
  );

  const bigBlind = local.bigBlind || 1;
  const currentBet = currentBetGlobal;
  const bets = (local.seats || []).map((seat) => Number(seat.bet) || 0);
  const computeMinRaiseTotal = () => {
    const prevBets = bets.filter((b) => b < currentBet);
    const previousMax = prevBets.length ? Math.max(...prevBets) : 0;
    const previousRaiseSize =
      previousMax < bigBlind ? bigBlind : Math.max(1, currentBet - previousMax);
    if (currentBet === 0) {
      return bigBlind;
    }
    return currentBet + previousRaiseSize;
  };
  const minRaiseTotal = computeMinRaiseTotal();
  const showRoundResults = !!(local.finished && local.result && local.result.breakdown);
  const showdownActive = !!(local.result && local.result.showdown);
  const breakdownStages = Array.isArray(local?.result?.breakdown)
    ? local.result.breakdown
    : [];

  const winnerBubbleMap = {};
  const showdownSummaryItems = [];
  const activePayoutStage = showRoundResults
    ? breakdownStages[Math.min(payoutStageIndex, Math.max(breakdownStages.length - 1, 0))]
    : null;
  if (activePayoutStage) {
    const winners = Array.isArray(activePayoutStage.winners)
      ? activePayoutStage.winners
      : [];
    const share = Math.floor((activePayoutStage.pot || 0) / Math.max(winners.length, 1));
    let remainder = (activePayoutStage.pot || 0) - share * winners.length;
    const orderedWinners = (local.seats || [])
      .filter((seat) => winners.includes(seat.socketId))
      .map((seat) => seat.socketId);

    orderedWinners.forEach((winnerId) => {
      const winnerHand = (activePayoutStage.hands || []).find(
        (hand) => hand.socketId === winnerId
      );
      const payout = share + (remainder > 0 ? 1 : 0);
      if (remainder > 0) remainder -= 1;
      const bubble = {
        id: `${payoutStageIndex}-${winnerId}`,
        order: payoutStageIndex,
        amount: payout,
        descr: winnerHand?.descr || "Winning hand",
        winnerId,
      };
      if (!winnerBubbleMap[winnerId]) winnerBubbleMap[winnerId] = [];
      winnerBubbleMap[winnerId].push(bubble);
      showdownSummaryItems.push(bubble);
    });
  }

  const renderPlayerIcon = (s, idx, side) => {
    const seatIndex = local.seats.findIndex((x) => x.socketId === s.socketId);
    // markers variable removed; markerSlots is now used for consistent layout
    const hasD = local.dealerIndex === seatIndex;
    const hasSB = local.smallBlindIndex === seatIndex;
    const hasBB = local.bigBlindIndex === seatIndex;
    // Always render 3 marker slots for consistent height
    const markerSlots = [
      hasD ? "D" : null,
      hasSB ? "SB" : null,
      hasBB ? "BB" : null,
    ];
    return (
      <div
        key={s.socketId}
        className={"relative flex flex-col items-center justify-center p-2 w-full"}
      >
        <div className="flex flex-col items-center w-full">
          <div className="text-white w-full">
            <div className="font-medium w-full" style={{ maxWidth: 100, minWidth: 0, overflow: "hidden", margin: "0 auto" }}>
              <AutoFitText text={s.username || "Player"} min={10} max={13} style={{ width: "100%" }} />
            </div>
          </div>

          <div className="flex flex-col items-center justify-center mt-1 w-full">
            <span className="text-xs text-white/90">{s.chips}</span>
            <span className="flex items-center justify-center gap-1 mt-1 min-h-[18px] w-full">
              {markerSlots.map((m, i) => {
                if (!m) {
                  // Render invisible placeholder for consistent height/spacing
                  return (
                    <div key={i} style={{ width: 24, height: 16, opacity: 0 }} />
                  );
                }
                const cls =
                  m === "D"
                    ? "flex items-center justify-center text-[10px] font-semibold px-1 py-0.5 bg-white dark:bg-gray-200 text-black dark:text-black border border-gray-300 dark:border-gray-600 rounded-sm"
                    : m === "SB"
                    ? "flex items-center justify-center text-[10px] font-semibold px-1 py-0.5 bg-blue-500 dark:bg-blue-400 text-white rounded-sm"
                    : m === "BB"
                    ? "flex items-center justify-center text-[10px] font-semibold px-1 py-0.5 bg-red-500 dark:bg-red-400 text-white rounded-sm"
                    : "flex items-center justify-center text-[10px] font-semibold px-1 py-0.5 bg-yellow-300 dark:bg-yellow-500 text-black rounded-sm";
                return (
                  <div key={i} className={cls} style={{ width: 24, height: 16, textAlign: 'center' }}>
                    {m}
                  </div>
                );
              })}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderCards = (s) => {
    if (!s) return null;
    // Don't show cards for eliminated players (chips <= 0 and not all-in)
    if ((s.chips || 0) <= 0 && !s.allIn) return null;
    const isMe = socket && socket.id === s.socketId;
    const isPeek = peekFor === s.socketId;
    const cards = s.cards || [];
    const folded = !!s.folded;
    const revealAllShowdownCards = showdownActive && !folded && cards.length === 2;
    const seatIsPeeking = !!(local?.peekingPlayers && local.peekingPlayers[s.socketId]);
    // Determine if server requested these cards to be revealed
    const reveals = (local && local.result && local.result.reveals) || {};
    const revealedIndexes = Array.isArray(reveals[s.socketId])
      ? reveals[s.socketId]
      : [];

    // win probability (percent) if provided by server
    const probs = (local && local.result && local.result.probabilities) || {};
    const pct =
      typeof probs[s.socketId] === "number"
        ? Math.round(probs[s.socketId])
        : null;

    if (isMe) {
      // compute action state for local controls
      const mySeatIdx = (local.seats || []).findIndex(
        (ss) => ss.socketId === (socket && socket.id)
      );
      const mySeat = mySeatIdx !== -1 ? (local.seats || [])[mySeatIdx] : {};
      const currentBet = local.currentBet || 0;
      const toCall = Math.max(0, currentBet - (mySeat.bet || 0));
      const isMyTurn =
        mySeatIdx !== -1 && local.currentPlayerIndex === mySeatIdx;
      // when the hand is finished, showdown, or everyone remaining is all-in, disable actions
      const bettingClosed =
        bettingClosedGlobal ||
        !!local.finished ||
        local.stage === "showdown" ||
        !!(local.result && local.result.showdown);
      // checkDisabled no longer needed (buttons are hidden when not your turn)
      const doAction = onGameAction || sendAction || (() => {});
      const guarded = (action) => {
        if (!isMyTurn || bettingClosed) return;
        doAction(action);
      };

      // If the computed minimum raise total is greater than the chips the player has,
      // the player cannot make a legal raise and must go All-in instead.
      // raiseNotAfforded no longer needed (buttons are hidden when not your turn)

      return (
        <div className="flex items-center gap-4">
          {/* Left vertical stack: Fold, Check or Call (Call shows amount) - only render when it's your turn and betting is open */}
          {/* Fold/Check/Call are always legal regardless of whether anyone could still respond to a raise */}
          {!bettingClosed && isMyTurn && (
            <div className="flex flex-col space-y-2">
              <button
                className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white dark:bg-red-500 dark:hover:bg-red-600"
                onClick={() => guarded({ type: "fold" })}
              >
                Fold
              </button>
              {/* show Call when there's an amount to call, otherwise show Check */}
              {toCall > 0 ? (
                <button
                  className="px-3 py-1 rounded bg-green-600 hover:bg-green-700 text-white dark:bg-green-500 dark:hover:bg-green-600"
                  onClick={() => guarded({ type: "call" })}
                >
                  {`Call ${toCall}`}
                </button>
              ) : (
                <button
                  className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-600"
                  onClick={() => guarded({ type: "check" })}
                >
                  Check
                </button>
              )}
            </div>
          )}

          {/* Center: cards + peek toggle */}
          <div
            className="flex flex-col items-center cursor-pointer gap-2"
            onClick={() => {
              if (showdownActive) return;
              if (isPeek) return;
              setPeekFor(s.socketId);
              actionHandlerRef.current({ type: "peekCards", active: true });
            }}
            title={isPeek ? "Hide your cards" : "Peek at your cards"}
          >
            {pct !== null && (
              <div className="text-s bold text-red-500 dark:text-yellow-200">
                {pct}%
              </div>
            )}
            <style>{`
              .deal-animate {
                opacity: 0;
                position: relative;
                z-index: 2;
                animation: dealIn 0.7s cubic-bezier(.7,.2,.3,1.2) forwards;
              }
              .deal-animate.deal-1 { animation-delay: 0.1s; }
              .deal-animate.deal-2 { animation-delay: 0.3s; }
              @keyframes dealIn {
                0% { opacity: 0; transform: var(--deal-transform, translateY(-120vh)) scale(1); }
                60% { opacity: 1; transform: translateY(-50vh) scale(1); }
                100% { opacity: 1; transform: translateY(0) scale(1); }
              }
            `}</style>
            <div className="flex items-center gap-2">
              {(cards.length ? cards : [null, null]).map((c, i) => {
                // Animate only at the start of a round (pre-flop, round === 1, no community cards)
                const shouldAnimateDeal =
                  local.round === 1 &&
                  (!local.communityCards || local.communityCards.length === 0);
                const cardClass = shouldAnimateDeal
                  ? `deal-animate deal-${i + 1}`
                  : "";
                return (
                  <span key={i} className={cardClass}>
                    {isPeek || revealAllShowdownCards || revealedIndexes.includes(i)
                      ? renderCompactCard(c, i, "small")
                      : renderCompactBack(
                          `back-${s.socketId}-${i}`,
                          "small",
                          folded,
                          false
                        )}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Right vertical stack: Raise, All-in (only render when it's your turn and betting is open) */}
          {!bettingClosed && isMyTurn && (
            <div className="flex flex-col space-y-2">
              <button
                className="px-3 py-1 rounded bg-yellow-600 hover:bg-yellow-700 text-white dark:bg-yellow-500 dark:hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!canIncreaseBetGlobal}
                onClick={() => {
                  // open raise popup; pick a sensible default respecting rule-based minimums
                  const max = (mySeatGlobal && (mySeatGlobal.chips || 0)) || 0;
                  // default should be at least the amount to call or the rule-minimum total
                  const defaultDesired = Math.max(
                    toCallGlobal + (mySeatGlobal.bet || 0),
                    minRaiseTotal
                  );
                  // raiseAmount stores the amount the player will put in total for the action (absolute total)
                  const defaultVal = Math.min(
                    max,
                    Math.max(
                      defaultDesired,
                      toCallGlobal + (mySeatGlobal.bet || 0)
                    )
                  );
                  setRaiseAmount(defaultVal);
                  setShowRaisePopup(true);
                }}
                title={
                  !canIncreaseBetGlobal
                    ? "No active opponent can respond to a raise"
                    : (minRaiseTotal || 0) > ((mySeatGlobal && mySeatGlobal.chips) || 0)
                    ? "Insufficient chips for minimum raise — use All-in"
                    : "Raise"
                }
              >
                Raise
              </button>
              <button
                className="px-3 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white dark:bg-indigo-500 dark:hover:bg-indigo-600"
                onClick={() =>
                  guarded({ type: "allin", amount: mySeat.chips || undefined })
                }
              >
                All-in
              </button>
            </div>
          )}
        </div>
      );
    }
    // other players: show revealed cards if server requested, otherwise backs
    return (
      <div className="flex flex-col items-center gap-2">
        {pct !== null && (
          <div className="text-s bold text-red-500 dark:text-yellow-200">
            {pct}%
          </div>
        )}
        <style>{`
          .deal-animate {
            opacity: 0;
            transform: translateY(-120vh) scale(0.8);
            animation: dealIn 0.7s cubic-bezier(.7,.2,.3,1.2) forwards;
          }
          .deal-animate.deal-1 { animation-delay: 0.1s; }
          .deal-animate.deal-2 { animation-delay: 0.3s; }
          @keyframes dealIn {
            0% { opacity: 0; transform: translateY(-120vh) scale(1); }
            60% { opacity: 1; transform: translateY(10vh) scale(1); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>
        <div className="flex items-center gap-2">
          {(cards.length ? cards : [null, null]).map((c, i) => {
            // Animate only at the start of a round (pre-flop, round === 1, no community cards)
            const shouldAnimateDeal =
              local.round === 1 &&
              (!local.communityCards || local.communityCards.length === 0);
            let cardClass = "";
            if (shouldAnimateDeal) {
              // Find seat index for this player in the table order
              const seatIndex = (local.seats || []).findIndex(
                (seat) => seat.socketId === s.socketId
              );
              const dealerIndex = local.dealerIndex ?? 0;
              const N = (local.seats || []).length;
              // Calculate angle for each seat around the table (circle)
              const anglePerSeat = 360 / Math.max(N, 1);
              // Dealer position is origin (0deg), others are offset
              const dealerAngle = dealerIndex * anglePerSeat;
              const playerAngle = seatIndex * anglePerSeat;
              // Calculate X/Y offset for animation start (polar coordinates)
              const radius = 40; // vw units for effect
              const rad = ((playerAngle - dealerAngle) * Math.PI) / 180;
              const x = Math.round(Math.cos(rad) * radius);
              const y = Math.round(Math.sin(rad) * radius);
              // Deal order: first card to each player, then second card to each player
              // Animation delay: (seatIndex + i*N) * baseDelay
              const baseDelay = 0.25; // seconds
              const delay = (seatIndex + i * N) * baseDelay;
              cardClass = `deal-animate`;
              // Inline style for animation delay and custom transform
              return (
                <span
                  key={i}
                  className={cardClass}
                  style={{
                    animationDelay: `${delay}s`,
                    "--deal-transform": `translate(${x}vw, ${y}vh)`,
                  }}
                >
                  {revealAllShowdownCards || revealedIndexes.includes(i)
                    ? renderCompactCard(c, `card-${s.socketId}-${i}`, "small")
                    : renderCompactBack(
                        `back-${s.socketId}-${i}`,
                        "small",
                        false,
                        seatIsPeeking
                      )}
                </span>
              );
            }
            return (
              <span key={i}>
                {revealAllShowdownCards || revealedIndexes.includes(i)
                  ? renderCompactCard(c, `card-${s.socketId}-${i}`, "small")
                  : renderCompactBack(
                      `back-${s.socketId}-${i}`,
                      "small",
                      false,
                      seatIsPeeking
                    )}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSeatPanel = (s, content, extraClasses = "", widthClass = "max-w-[190px]") => {
    const actionMessage = (() => {
      if (!visibleAction || visibleAction.socketId !== s.socketId) return "";
      if (visibleAction.type === "peek") return "";
      if (visibleAction.type === "fold") return `${visibleAction.username || "Player"} folded`;
      if (visibleAction.type === "check") return `${visibleAction.username || "Player"} checked`;
      if (visibleAction.type === "call") {
        const amount = Number(visibleAction.amount) || 0;
        return `${visibleAction.username || "Player"} called ${amount}`;
      }
      if (visibleAction.type === "raise" || visibleAction.type === "bet") {
        const amount = Number(visibleAction.amount) || 0;
        const total = Number(visibleAction.total) || 0;
        return total > 0
          ? `${visibleAction.username || "Player"} raised by ${amount} to ${total}`
          : `${visibleAction.username || "Player"} raised by ${amount}`;
      }
      if (visibleAction.type === "allin") {
        const amount = Number(visibleAction.amount) || 0;
        return amount > 0
          ? `${visibleAction.username || "Player"} went all-in for ${amount}`
          : `${visibleAction.username || "Player"} went all-in`;
      }
      return "";
    })();
    const seatIndex = local.seats.findIndex((x) => x.socketId === s.socketId);
    // if betting is closed, there should be no current player highlight
    const isCurrent = !bettingClosedGlobal && local.currentPlayerIndex === seatIndex;
    const seatIsPeeking = !!(local?.peekingPlayers && local.peekingPlayers[s.socketId]);
    // Show latest move message if this player performed the last action
    const showActionMsg =
      visibleAction &&
      !seatIsPeeking &&
      visibleAction.socketId === s.socketId &&
      !!actionMessage;
    const isFolded = !!s.folded;
    const isEliminated = (s.chips || 0) <= 0 && !s.allIn;
    const payoutBubbles = winnerBubbleMap[s.socketId] || [];
    // Gray out if folded or eliminated (but NOT all-in)
    const grayClass = isFolded || isEliminated ? ' opacity-60 grayscale' : '';
    // Bubble offset tuning
    const payoutBubbleOffset = 38; // px higher
    const payoutBubbleSide = 90; // px further right
    return (
      <div
        key={s.socketId}
        className={`relative w-full ${widthClass} rounded-[12px] border border-white/10 px-3 py-3 text-white shadow-[0_14px_32px_rgba(0,0,0,0.24)] backdrop-blur-[2px] ${extraClasses}${isCurrent ? ' animate-flash-yellow-border bg-yellow-200/60' : ''}${grayClass}`}
        style={isCurrent ? { ...seatShellStyle, boxShadow: '0 0 0 3px #fde04799, 0 2px 8px #0002' } : seatShellStyle}
      >
        {/* Flashing yellow border animation */}
        {isCurrent && (
          <style>{`
            @keyframes flash-yellow-border {
              0% { box-shadow: 0 0 0 0 #fde04799, 0 2px 5px #0002; }
              50% { box-shadow: 0 0 0 4px #fde047cc, 0 2px 5px #0002; }
              100% { box-shadow: 0 0 0 0 #fde04799, 0 2px 5px #0002; }
            }
            .animate-flash-yellow-border {
              animation: flash-yellow-border 2.2s cubic-bezier(.4,0,.6,1) infinite;
            }
          `}</style>
        )}
        {/* Show latest move message as a floating bubble if this player acted */}
        {showActionMsg && (
          <div className="absolute left-1/2 -top-7 -translate-x-1/2 z-30 px-3 py-1 rounded-xl bg-white/90 text-slate-900 text-xs font-semibold shadow-lg border border-yellow-300 animate-fade-in" style={{ pointerEvents: 'none', minWidth: 80, maxWidth: 180, textAlign: 'center' }}>
            {actionMessage}
          </div>
        )}
        {payoutBubbles.length > 0 && (
          <div
            className="pointer-events-none absolute z-30 flex w-[132px] flex-col gap-2"
            style={{
              right: `-${payoutBubbleSide}px`,
              top: `calc(50% - ${payoutBubbleOffset}px)`,
              transform: 'translateY(-50%)',
            }}
          >
            {payoutBubbles.map((bubble, index) => (
              <div
                key={bubble.id}
                className="rounded-[22px] border border-yellow-300/60 bg-[linear-gradient(135deg,rgba(255,240,170,0.94),rgba(255,214,74,0.94))] px-3 py-3 text-[12px] font-semibold text-amber-950 shadow-[0_12px_24px_rgba(0,0,0,0.22)] animate-fade-in"
                style={{
                  transform: `translateX(${index * 6}px)`,
                  animationDelay: `${index * 0.16}s`,
                }}
              >
                <div className="uppercase tracking-[0.14em] text-[10px] text-amber-900/75">
                  {bubble.order === 0 ? "Main Pot" : `Side Pot ${bubble.order}`}
                </div>
                <div className="mt-0.5 leading-tight">{bubble.descr}</div>
                <div className="mt-1 text-[13px] font-bold text-amber-950">+{bubble.amount}</div>
              </div>
            ))}
          </div>
        )}
        {content}
      </div>
    );
  };

  const renderNextRoundControl = () => {
    try {
      const hostId = local && (local._roomHostSocketId || local.hostSocketId);
      const hostSeat = (local.seats || []).find((s) => s.socketId === hostId) || {};
      const hostName = hostSeat.username || hostId || "host";

      if (gameOver) {
        return null;
      }

      if (hostId && !(socket && socket.id === hostId)) {
        return (
          <div className="text-sm text-white/75">
            {`Waiting on ${hostName}`}
            {nextCountdown ? ` (${nextCountdown})` : ""}
          </div>
        );
      }

      return (
        <button
          className="rounded-xl bg-yellow-300 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-yellow-200"
          onClick={() => {
            const doAction = onGameAction || sendAction || (() => {});
            doAction({ type: "nextRound" });
          }}
          title={"Start next round now"}
        >
          {`Next Round${nextCountdown ? ` (${nextCountdown})` : ""}`}
        </button>
      );
    } catch (e) {
      return null;
    }
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center px-2 py-4 sm:px-4">
      {/* Latest move message is now shown near the acting player */}

      {/* Info container above the board */}
      <div className="w-full max-w-5xl mb-3">
        <div className="rounded-3xl border border-emerald-900/30 bg-gradient-to-br from-white/80 via-emerald-50 to-emerald-100/80 p-3 shadow-sm dark:border-emerald-900/60 dark:from-emerald-950 dark:via-emerald-900 dark:to-emerald-950/40 flex flex-col items-start">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-900/80 dark:text-emerald-200 mb-1 sm:text-xs sm:tracking-[0.2em]">Poker Info</div>
          <div className="flex flex-col items-start text-emerald-900 dark:text-emerald-100 text-xs sm:text-sm">
            <span className="font-semibold">Round {local?.round && local.round > 0 ? local.round : 1}</span>
            <span>Small Blind {local.smallBlind || 0}, Big Blind {local.bigBlind || 0}</span>
            <span>Blinds increase in: {formatBlindCountdown(blindCountdown)}</span>
          </div>
        </div>
      </div>

      <div
        className={`w-full max-w-5xl rounded-[30px] border border-emerald-900/40 p-3 text-white shadow-[0_24px_70px_rgba(0,0,0,0.22)] transition-transform duration-500 ease-out sm:p-5 ${showRoundResults ? "origin-top scale-[0.95] sm:scale-[0.97]" : "scale-100"}`}
        style={boardShellStyle}
      >
      {/* Top players row should be above the table */}
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center mb-4">
          {top.map((s) => (
            <div key={s.socketId} className="mx-3">
              {renderSeatPanel(
                s,
                <>
                  {renderPlayerIcon(s)}
                  <div className="mt-2 flex justify-center">{renderCards(s)}</div>
                </>,
                "",
                "max-w-[300px]"
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="w-full flex flex-row items-stretch gap-y-3">
        {/* Left column (players) - fixed width */}
        <div className="flex flex-col items-center justify-center space-y-3 w-[106px] min-w-[93px]">
          {left.map((s, idx) => (
            <div key={s.socketId} className="flex items-center w-full justify-center">
              {renderSeatPanel(
                s,
                <>
                  {renderPlayerIcon(s, idx, "left")}
                  <div className="mt-2 flex justify-center">{renderCards(s)}</div>
                </>,
                "mr-1",
                "max-w-[106px]"
              )}
            </div>
          ))}
        </div>

        {/* Center table area - flexible, much narrower on mobile */}
        <div className="flex flex-col items-center justify-center flex-1 min-w-[120px] sm:min-w-[220px] px-1">
          <div
            className="w-full rounded-[30px] border border-white/10 p-4 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] flex flex-col items-center"
            style={{
              ...tableSurfaceStyle,
              minHeight: Math.min(420, 100 + seats.length * 28),
            }}
          >
            {showRoundResults && (
              <>
                <style>{`
                  @keyframes showdownPulse {
                    0% { opacity: 0; transform: translateY(10px) scale(0.98); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                  }
                `}</style>
                {/* Only keep the yellow bubbles (summary items) if needed, but remove all text and result summary */}
                {breakdownStages.length > 1 && (
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-yellow-200/70">
                    {payoutStageIndex === 0
                      ? `Main Pot 1 of ${breakdownStages.length}`
                      : `Side Pot ${payoutStageIndex} of ${breakdownStages.length - 1}`}
                  </div>
                )}
                <div className="mt-2 flex justify-center">
                  {renderNextRoundControl()}
                </div>
              </>
            )}
            <div className="text-sm text-gray-200 mb-1">
              <div className="flex flex-col items-center text-sm text-white/80 dark:text-white/80">
                <div className="font-semibold">Pot: {displayedPot}</div>
              </div>
            </div>
            <div className="flex flex-col items-center space-y-2">
              {(() => {
                const community = local.communityCards || [];
                if (!community.length)
                  return (
                    <div className="text-sm text-gray-200">
                      No community cards
                    </div>
                  );
                return (
                  <>
                    <style>{`
                      .card-animate {
                        opacity: 0;
                        transform: translateX(-120vw) scale(0.8);
                        animation: cardIn 0.7s cubic-bezier(.7,.2,.3,1.2) forwards;
                      }
                      .card-animate.card-flop { animation-delay: 0.1s; }
                      .card-animate.card-flop2 { animation-delay: 0.2s; }
                      .card-animate.card-flop3 { animation-delay: 0.3s; }
                      .card-animate.card-turn { animation-delay: 0.5s; }
                      .card-animate.card-river { animation-delay: 0.7s; }
                      @keyframes cardIn {
                        0% { opacity: 0; transform: translateX(-120vw) scale(1); }
                        60% { opacity: 1; transform: translateX(-50vw) scale(1); }
                        100% { opacity: 1; transform: translateX(0) scale(1); }
                      }
                    `}</style>
                    <div className="flex items-center justify-center gap-2">
                      {community.slice(0, 3).map((c, i) => (
                        <span
                          key={i}
                          className={`card-animate card-flop${i + 1}`}
                        >
                          {renderCompactCard(c, i, "small")}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      {community.slice(3, 4).map((c, i) => (
                        <span key={3 + i} className="card-animate card-turn">
                          {renderCompactCard(c, 3 + i, "small")}
                        </span>
                      ))}
                      {community.slice(4, 5).map((c, i) => (
                        <span key={4 + i} className="card-animate card-river">
                          {renderCompactCard(c, 4 + i, "small")}
                        </span>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right column (players) - fixed width */}
        <div className="flex flex-col items-center justify-center space-y-3 w-[106px] min-w-[93px]">
          {right.map((s, idx) => (
            <div key={s.socketId} className="flex items-center w-full justify-center">
              {renderSeatPanel(
                s,
                <>
                  {renderPlayerIcon(s, idx, "right")}
                  <div className="mt-2 flex justify-center">{renderCards(s)}</div>
                </>,
                "ml-1",
                "max-w-[106px]"
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom players should be below the table */}
      <div className="w-full flex flex-col items-center mt-4">
        <div className="w-full flex justify-center">
          {bottom.map((s) => (
            <div key={s.socketId} className="mx-3">
              {renderSeatPanel(
                s,
                <>
                  {renderPlayerIcon(s)}
                  <div className="mt-2 flex justify-center">{renderCards(s)}</div>
                </>,
                "",
                "max-w-[176px]"
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Controls remain at bottom */}

      <div className="w-full max-w-5xl mt-6">
        <div className="flex flex-wrap items-center justify-center gap-2">
          {null}
        </div>
      </div>
      {/* Raise popup using shared Modal component */}
      <Modal
        show={showRaisePopup}
        title={`Raise`}
        onClose={() => setShowRaisePopup(false)}
      >
        <div className="p-4">
          <div className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            Slide to choose raise amount
          </div>
          <div className="mb-3">
            {/* Preset buttons */}
            <div className="flex gap-2 mb-2">
              {[minRaiseTotal, minRaiseTotal * 2, minRaiseTotal * 3].map((amt, idx) => (
                <button
                  key={amt}
                  className="px-2 py-1 rounded bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs"
                  onClick={() => setRaiseAmount(Math.min(amt, (mySeatGlobal.chips || 0)))}
                  disabled={amt > (mySeatGlobal.chips || 0)}
                >
                  {amt}
                </button>
              ))}
              <button
                className="px-2 py-1 rounded bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs"
                onClick={() => setRaiseAmount(mySeatGlobal.chips || 0)}
              >
                All-in
              </button>
            </div>
            {/* Slider */}
            <input
              type="range"
              min={Math.max(minRaiseTotal, toCallGlobal + (mySeatGlobal.bet || 0))}
              max={(mySeatGlobal && (mySeatGlobal.chips || 0)) || 0}
              value={raiseAmount}
              onChange={(e) => setRaiseAmount(Number(e.target.value))}
              className="w-full mb-2"
            />
            {/* Text box for direct entry */}
            <input
              type="number"
              min={Math.max(minRaiseTotal, toCallGlobal + (mySeatGlobal.bet || 0))}
              max={(mySeatGlobal && (mySeatGlobal.chips || 0)) || 0}
              value={raiseAmount}
              onChange={e => {
                let val = Number(e.target.value);
                const min = Math.max(minRaiseTotal, toCallGlobal + (mySeatGlobal.bet || 0));
                const max = (mySeatGlobal && (mySeatGlobal.chips || 0)) || 0;
                if (isNaN(val)) val = min;
                if (val < min) val = min;
                if (val > max) val = max;
                setRaiseAmount(val);
              }}
              className="w-full mt-2 px-2 py-1 rounded border border-gray-300 dark:border-gray-700 text-sm"
              placeholder="Type amount"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <div>Min: {Math.max(minRaiseTotal, toCallGlobal + (mySeatGlobal.bet || 0))}</div>
              <div>Max: {(mySeatGlobal && (mySeatGlobal.chips || 0)) || 0}</div>
            </div>
            <div className="text-center text-sm font-medium mt-2">{`Raise: ${raiseAmount}`}</div>
            {/* Confirm / Cancel buttons */}
            <div className="flex gap-2 mt-4 justify-center">
              <button
                className="px-3 py-2 rounded bg-yellow-600 hover:bg-yellow-700 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed w-1/2"
                disabled={raiseAmount < Math.max(minRaiseTotal, toCallGlobal + (mySeatGlobal.bet || 0)) || raiseAmount > ((mySeatGlobal && (mySeatGlobal.chips || 0)) || 0)}
                onClick={() => {
                  if (raiseAmount >= Math.max(minRaiseTotal, toCallGlobal + (mySeatGlobal.bet || 0)) && raiseAmount <= ((mySeatGlobal && (mySeatGlobal.chips || 0)) || 0)) {
                    const doAction = onGameAction || sendAction || (() => {});
                    doAction({ type: "raise", amount: raiseAmount });
                    setShowRaisePopup(false);
                  }
                }}
              >
                Confirm Raise
              </button>
              <button
                className="px-3 py-2 rounded bg-gray-400 hover:bg-gray-500 text-white font-semibold text-sm w-1/2"
                onClick={() => setShowRaisePopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>
      </div>
    </div>
  );
}
